import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  AccountControllerService,
  AccountResponseDto,
  BASE_PATH,
  CreateAccountDTO,
  EInvoiceControllerService,
  FailedIssuanceControllerService,
  FailedIssuanceReplayResultDTO,
} from '@app/api/einvoice-service';

/** Backend `errorCode` values this feature renders as localized messages. */
const ERROR_CODE_TRANSLATION_KEYS: Record<string, string> = {
  PROVIDER_QUOTA_EXHAUSTED: 'e_invoice.account.messages.error.quota_exhausted',
  ACCOUNT_COMPANY_REQUIRED:
    'e_invoice.account.messages.error.company_required_when_active',
  ACCOUNT_TEMPLATE_REQUIRED:
    'e_invoice.account.messages.error.template_required_when_active',
  ACCOUNT_SERIES_REQUIRED:
    'e_invoice.account.messages.error.series_required_when_active',
  ACCOUNT_COMPANY_ACTIVE_DUPLICATE:
    'e_invoice.account.messages.error.company_active_duplicate',
};

interface ErrorBody {
  errorCode?: string;
  message?: string;
  data?: Record<string, unknown>;
}

export interface EInvoiceAccount {
  id: number;
  providerId: number;
  providerCode: string;
  providerName: string;
  companyId?: number;
  companyName?: string;
  username?: string;
  invoiceTemplate?: string;
  invoiceSeries?: string;
  status: boolean;
  password?: string;
  issuedCount: number;
  quotaExhausted: boolean;
  exhaustedAt?: string;
  issuedCountAtExhaustion?: number;
  lastIssuedAt?: string;
  pendingFailedCount: number;
  /** Orders one retry processes; 0 when the backend doesn't report it. */
  retryBatchSize?: number;
}

/**
 * Account row. `retryBatchSize` is declared locally until the einvoice-service
 * client is regenerated with the field; drop the intersection afterwards.
 */
type AccountRowDto = AccountResponseDto & { retryBatchSize?: number };

/**
 * Replay result. `alreadyRunning` is declared locally until the einvoice-service
 * client is regenerated with the field; drop the intersection afterwards.
 */
export type RetryPendingResult = FailedIssuanceReplayResultDTO & {
  alreadyRunning?: boolean;
};

export interface EInvoiceAccountPageRequest {
  providerId?: number;
  status?: boolean;
  quotaExhausted?: boolean;
  page: number;
  size: number;
}

export interface EInvoiceAccountPageResponse {
  pageContent: EInvoiceAccount[];
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
}

export interface EInvoiceAccountCreateRequest {
  providerId: number;
  userName: string;
  password: string;
  companyId?: number;
  companyName?: string;
  invoiceTemplate?: string;
  invoiceSeries?: string;
  status?: boolean;
}

export interface EInvoiceAccountUpdateRequest {
  providerId: number;
  userName?: string;
  password?: string;
  companyId?: number;
  companyName?: string;
  invoiceTemplate?: string;
  invoiceSeries?: string;
  status?: boolean;
}

export interface EInvoiceTemplateRequest {
  invoiceType: string;
  username: string;
  password: string;
  providerCode: string;
  /** When set, the backend annotates each template with its usage/quota state. */
  accountId?: number;
}

export interface EInvoiceTemplateItemResponse {
  templateCode: string;
  invoiceSeri: string;
  originalTemplateCode: string;
  issuedCount?: number;
  quotaExhausted?: boolean;
  exhaustedAt?: string;
  lastIssuedAt?: string;
}

export interface EInvoiceTemplatePreviewRequest {
  templateCode: string;
  invoiceSeries: string;
  username: string;
  password: string;
  providerCode: string;
  /** With no password, the backend previews using this saved account's credentials. */
  accountId?: number;
}

@Injectable({
  providedIn: 'root',
})
export class EInvoiceAccountService {
  private readonly http = inject(HttpClient);
  private readonly basePath = inject(BASE_PATH);
  private readonly apiService = inject(AccountControllerService);
  private readonly invoiceService = inject(EInvoiceControllerService);
  private readonly failedIssuanceService = inject(
    FailedIssuanceControllerService,
  );

  /**
   * Translation key for a backend `errorCode`, or `null` when the code is
   * missing/unknown so callers can show their generic fallback.
   */
  getErrorTranslationKey(error: unknown): string | null {
    const errorCode = this.getErrorBody(error)?.errorCode;
    return (errorCode && ERROR_CODE_TRANSLATION_KEYS[errorCode]) || null;
  }

  /** Interpolation params the backend attaches to an error (e.g. quota info). */
  getErrorParams(error: unknown): Record<string, unknown> {
    return this.getErrorBody(error)?.data ?? {};
  }

  /**
   * Calls sent with `Accept: text/plain` use `responseType: 'text'`, so the
   * error body arrives as a raw JSON string instead of an object.
   */
  private getErrorBody(error: unknown): ErrorBody | null {
    const body = (error as { error?: unknown } | null)?.error;
    if (typeof body !== 'string') {
      return (body as ErrorBody) ?? null;
    }
    try {
      return JSON.parse(body) as ErrorBody;
    } catch {
      return null;
    }
  }

  private mapToAccount(dto: AccountRowDto): EInvoiceAccount {
    return {
      id: dto.id ?? 0,
      providerId: dto.provider?.providerId ?? 0,
      providerCode: dto.provider?.providerCode ?? '',
      providerName: dto.provider?.providerName ?? '',
      companyId: dto.company?.companyId ?? 0,
      companyName: dto.company?.companyName ?? '',
      username: dto.userName ?? '',
      password: dto.password ?? '',
      invoiceTemplate: dto.templateCode ?? '',
      invoiceSeries: dto.invoiceSeries ?? '',
      status: dto.status !== false,
      issuedCount: dto.issuedCount ?? 0,
      quotaExhausted: dto.quotaExhausted === true,
      exhaustedAt: dto.exhaustedAt,
      issuedCountAtExhaustion: dto.issuedCountAtExhaustion,
      lastIssuedAt: dto.lastIssuedAt,
      pendingFailedCount: dto.pendingFailedCount ?? 0,
      retryBatchSize: dto.retryBatchSize ?? 0,
    };
  }

  retryPendingOrders(accountId: number): Observable<RetryPendingResult> {
    return this.failedIssuanceService.replay(accountId, undefined, undefined, {
      httpHeaderAccept: 'application/json' as any,
    });
  }

  searchAccounts(
    payload: EInvoiceAccountPageRequest,
  ): Observable<EInvoiceAccountPageResponse> {
    return this.apiService
      .getAccounts(
        payload.providerId,
        payload.status,
        payload.quotaExhausted,
        payload.page,
        payload.size,
        undefined,
        undefined,
        { httpHeaderAccept: 'application/json' as any },
      )
      .pipe(
        map((response) => ({
          pageContent: (response.pageContent || []).map((dto) =>
            this.mapToAccount(dto),
          ),
          totalElements: response.totalElements || 0,
          totalPages: response.totalPages || 0,
          page: response.number ?? payload.page ?? 0,
          size: response.size || payload.size,
        })),
      );
  }

  getAccountById(id: number, providerId: number): Observable<EInvoiceAccount> {
    return this.apiService
      .getAccount(id, providerId, undefined, undefined, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(map((dto) => this.mapToAccount(dto)));
  }

  /** True when another active account already belongs to the company. */
  hasActiveAccountForCompany(
    companyId: number,
    excludeAccountId?: number,
  ): Observable<boolean> {
    let params = new HttpParams().set('companyId', companyId);
    if (excludeAccountId != null) {
      params = params.set('excludeAccountId', excludeAccountId);
    }
    return this.http.get<boolean>(`${this.basePath}/v1/accounts/exists`, {
      params,
    });
  }

  createAccount(payload: EInvoiceAccountCreateRequest): Observable<object> {
    const dto: CreateAccountDTO = {
      providerId: payload.providerId,
      userName: payload.userName,
      password: payload.password,
      companyId: payload.companyId,
      companyName: payload.companyName,
      templateCode: payload.invoiceTemplate,
      invoiceSeries: payload.invoiceSeries,
      status: payload.status,
    };
    return this.apiService.createAccount(dto, undefined, undefined, {
      httpHeaderAccept: 'text/plain' as any,
    });
  }

  updateAccount(
    id: number,
    payload: EInvoiceAccountUpdateRequest,
  ): Observable<object> {
    const dto = {
      providerId: payload.providerId,
      userName: payload.userName,
      password: payload.password,
      companyId: payload.companyId,
      companyName: payload.companyName,
      templateCode: payload.invoiceTemplate,
      invoiceSeries: payload.invoiceSeries,
      status: payload.status,
    } as CreateAccountDTO;
    return this.apiService.updateAccount(
      id,
      payload.providerId,
      dto,
      undefined,
      undefined,
      { httpHeaderAccept: 'text/plain' as any },
    );
  }

  deleteAccount(account: EInvoiceAccount): Observable<object> {
    return this.apiService.deleteAccount(
      account.id,
      account.providerId,
      undefined,
      undefined,
      {
        httpHeaderAccept: 'text/plain' as any,
      },
    );
  }

  getTemplates(
    payload: EInvoiceTemplateRequest,
  ): Observable<EInvoiceTemplateItemResponse[]> {
    const requestPayload = {
      invoiceType: '02GTTT',
      username: payload.username,
      password: payload.password,
      taxCode: payload.username,
      providerCode: payload.providerCode,
      accountId: payload.accountId,
    };
    return this.invoiceService.getTemplates(
      requestPayload,
      undefined,
      undefined,
      { httpHeaderAccept: 'application/json' as any },
    ) as Observable<EInvoiceTemplateItemResponse[]>;
  }

  getTemplatePreview(
    payload: EInvoiceTemplatePreviewRequest,
  ): Observable<Blob> {
    return this.invoiceService.getTemplatePreview(
      payload,
      undefined,
      undefined,
      {
        httpHeaderAccept: 'application/pdf' as any,
      },
    ) as unknown as Observable<Blob>;
  }
}
